import {
  FRIENDLY_CONNECTIVITY_MESSAGE,
  isConnectivityErrorMessage,
  isConnectivityFailure,
} from '@buxus/shared'

import { isAbpHttpError } from '../platform/abp/httpClient'

const GENERIC = 'Something went wrong. Please try again.'

type AbpErrorBody = {
  error?: {
    code?: string | null
    message?: string | null
    details?: string | null
  }
}

function tryParseAbpJson(text: string): AbpErrorBody | null {
  const start = text.indexOf('{')
  if (start < 0) return null
  try {
    return JSON.parse(text.slice(start)) as AbpErrorBody
  } catch {
    return null
  }
}

function mapAbpCodeToMessage(code: string, msg: string): string | null {
  if (code.includes('PasswordRequiresNonAlphanumeric') || code.includes('PasswordRequiresUpper')) {
    return 'Your password must be at least 8 characters and include an uppercase letter and a special character (for example !, @, or #).'
  }
  if (code.includes('PasswordRequiresDigit')) {
    return 'Your password must include at least one number.'
  }
  if (code.includes('PasswordRequiresLower')) {
    return 'Your password must include at least one lowercase letter.'
  }
  if (code.includes('PasswordTooShort') || msg.toLowerCase().includes('at least')) {
    return 'Your password does not meet the minimum requirements. Use at least 8 characters with an uppercase letter and a special character.'
  }
  if (code.includes('DuplicateUserName') || code.includes('DuplicateEmail')) {
    return 'An account with this email already exists. Try signing in or resetting your password.'
  }
  return null
}

/**
 * Map thrown `httpJson` errors and ABP Identity responses to short user-facing copy.
 * Unknown shapes fall back to {@link GENERIC}.
 */
export function friendlyRegistrationError(err: unknown): string {
  if (isConnectivityFailure(err)) {
    return FRIENDLY_CONNECTIVITY_MESSAGE
  }

  if (isAbpHttpError(err)) {
    const code = err.abpErrorCode ?? ''
    const msg = err.message.trim()
    const mapped = mapAbpCodeToMessage(code, msg)
    if (mapped) return mapped
    if (msg && !msg.includes('Volo.Abp') && msg.length < 200) return msg
    return GENERIC
  }

  if (!(err instanceof Error)) return GENERIC
  const raw = err.message
  if (isConnectivityErrorMessage(raw)) {
    return FRIENDLY_CONNECTIVITY_MESSAGE
  }
  const parsed = tryParseAbpJson(raw)
  const code = parsed?.error?.code ?? ''
  const msg = (parsed?.error?.message ?? '').trim()

  const mapped = mapAbpCodeToMessage(code, msg)
  if (mapped) return mapped
  if (msg && !msg.includes('Volo.Abp') && msg.length < 200) {
    return msg
  }
  return GENERIC
}

export { GENERIC as REGISTRATION_GENERIC_ERROR_MESSAGE }
